import React from "react";
import { isHlsVideoSource, useHlsVideo, type HlsVideoSource } from "./use-hls-video";
export type Status = "IDLE" | "PRELOADING" | "PRELOADED" | "ERROR";
type Props = {
  source?: HlsVideoSource;
  videoRef?: React.RefObject<HTMLVideoElement | null>;
};
export function useVideoPreload(props: Props) {
  const {
    source,
    videoRef
  } = props;
  const [status, setStatus] = React.useState<Status>("IDLE");
  const fallbackRef = React.useRef<HTMLVideoElement | null>(null);
  const resolvedRef = videoRef ?? fallbackRef;
  const isHls = isHlsVideoSource(source);
  const handleHlsReady = React.useCallback(() => setStatus("PRELOADED"), []);
  const handleHlsError = React.useCallback(() => setStatus("ERROR"), []);
  useHlsVideo({
    enabled: Boolean(source?.src),
    onError: handleHlsError,
    onReady: handleHlsReady,
    source,
    videoRef: resolvedRef
  });
  const preload = React.useCallback(() => {
    const videoUrl = source?.src;
    if (!videoUrl || !videoRef?.current) {
      return;
    }
    setStatus("PRELOADING");
    if (isHls) return;

    // based on http://dinbror.dk/blog/how-to-preload-entire-html5-video-before-play-solved/

    const req = new XMLHttpRequest();
    req.open("GET", videoUrl, true);
    req.responseType = "blob";
    let objectUrl: string | undefined;
    req.onload = e => {
      const xhr = e.target as XMLHttpRequest;
      if (xhr.status === 200) {
        const videoBlob = xhr.response;
        objectUrl = URL.createObjectURL(videoBlob);
        videoRef.current!.src = objectUrl;
        setStatus("PRELOADED");
      }
    };
    req.onerror = () => {
      setStatus("ERROR");
      console.error("Video preload failed");
    };
    req.send();
    return () => {
      req.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [isHls, source?.src, videoRef]);
  React.useEffect(() => {
    return preload();
  }, [preload]);
  return status;
}